/**
 * Parsing server timestamps correctly. One place, because getting it wrong is
 * silent and off by a whole day.
 *
 * THE BUG THIS EXISTS TO END (owner ask 2026-08-30)
 * -------------------------------------------------
 * saas_documents rows carry created_at/updated_at in the JSONB body as NAIVE
 * UTC — `2026-08-30T06:05:41.671018`, no offset — because the backend writes
 * `datetime.now(timezone.utc).replace(tzinfo=None).isoformat()`
 * (pg_document_store.py:24). ECMAScript parses a date-time string with no
 * offset as LOCAL time, so:
 *
 *     new Date('2026-08-30T06:05:41.671018')  ->  Aug 30, 06:05 PDT   WRONG
 *     new Date('2026-08-30T06:05:41.671018Z') ->  Aug 29, 23:05 PDT   right
 *
 * Production task 142 "Sleep" was created 2026-08-29 23:05 PDT and every
 * detail page rendered "Created: 8/30/2026". A day late, on every item
 * created between 00:00 and 07:00 UTC.
 *
 * The values ARE real UTC (production runs Etc/UTC), so no data migration is
 * needed — only correct parsing. The database columns are proper timestamptz
 * and were never wrong; this is a serialization-and-parsing problem, not a
 * storage one.
 */

/**
 * Parse a server timestamp, whether or not it carries an offset.
 *
 * Accepts both shapes the backend emits — naive (`...671018`) and
 * offset-aware (`...+00:00` / `...Z`) — and treats a missing offset as UTC,
 * which is what the backend means. Also tolerates the space separator
 * Postgres uses when a column is rendered directly.
 *
 * Returns null for empty or unparseable input, so callers can render a dash
 * instead of "Invalid Date".
 */
export function parseServerUtc(value?: string | null): Date | null {
  if (!value) return null
  const trimmed = String(value).trim()
  if (!trimmed) return null
  // Postgres renders a 2-digit offset ("+00"), ISO uses 4 ("+00:00" / "+0000"),
  // and the JSONB body uses none. Accept all three: requiring 4 digits made
  // `2026-08-30 06:05:41.671503+00` unparseable, so any surface handed a raw
  // column value rendered a dash instead of a time.
  const hasZone = /(Z|[+-]\d{2}(:?\d{2})?)$/i.test(trimmed)
  const d = new Date(hasZone ? trimmed : `${trimmed.replace(' ', 'T')}Z`)
  return Number.isNaN(d.getTime()) ? null : d
}

/** True when the string already states its offset. Useful for diagnostics. */
export function hasExplicitZone(value?: string | null): boolean {
  if (!value) return false
  return /(Z|[+-]\d{2}(:?\d{2})?)$/i.test(String(value).trim())
}
